import React from 'react';
import Styled from 'styled';
import classnames from 'classnames';
import { Mutation } from '@apollo/client/react/components';
import { FormattedMessage } from 'react-intl';

import SVGPureIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import addToGroup from 'four-nets/icons/svg/icon_add-to-group.svg';
import iconDelete from 'four-nets/icons/svg/icon_delete_2.svg';

import { AlertContext } from 'lib/alert-context/alert-context';
import Member from 'four-nets/photoblog/groups/group/member/member';
import UserInput from 'four-nets/photoblog/groups/users-input/users-input';

import addMutation, { AddModeratorsMutation, AddModeratorsMutationVariables } from './mutations/add-moderators.graphql';
import removeMutation, {
    RemoveModeratorMutation,
    RemoveModeratorMutationVariables,
} from './mutations/remove-moderator.graphql';
import { ModeratorsSettingsFragment } from './moderators-settings.graphql';
import { MemberRole } from './moderators-settings.graphql.types';
import defaultStyles from './moderators-settings.scss';

export interface Props extends ModeratorsSettingsFragment {
    styles: typeof defaultStyles;
}

interface State {
    newModerators: string[];
    value: string;
    key: number;
}

class ModeratorsSettings extends React.PureComponent<Props, State> {
    state: State = { newModerators: [], value: '', key: 0 };
    ref: React.RefObject<HTMLDivElement> = React.createRef();

    onChange = (users: string[], value: string) => {
        this.setState({
            newModerators: users,
            value,
        });
    };

    render() {
        const { styles, id, moderators, viewerMemberRole } = this.props;
        const { newModerators } = this.state;

        const currentModeratorsCount = (moderators && moderators.edges && moderators.edges.length) || 0;

        return (
            <AlertContext.Consumer>
                {({ confirm }) => (
                    <div className={styles.form}>
                        {viewerMemberRole === MemberRole.MEMBER_ROLE_CREATOR ? (
                            <React.Fragment>
                                <FormattedMessage id='GROUP_MODERATORS'>
                                    {message => <div className={styles.label}>{message}</div>}
                                </FormattedMessage>
                                <ul className={styles.existingModerators}>
                                    {moderators &&
                                        moderators.edges.map(edge =>
                                            edge && edge.node ? (
                                                <Mutation<RemoveModeratorMutation, RemoveModeratorMutationVariables>
                                                    mutation={removeMutation}
                                                >
                                                    {removeModerator => (
                                                        <Member {...edge.node!}>
                                                            <button
                                                                onClick={() => {
                                                                    const onAccept = () => {
                                                                        return removeModerator({
                                                                            variables: {
                                                                                input: {
                                                                                    id,
                                                                                    moderator: edge!.node!.user!
                                                                                        .username,
                                                                                },
                                                                            },
                                                                        });
                                                                    };

                                                                    confirm({
                                                                        acceptButtonLabel: (
                                                                            <FormattedMessage id='DELETE_MODERATOR' />
                                                                        ),
                                                                        body: (
                                                                            <React.Fragment>
                                                                                <FormattedMessage id='ARE_YOU_SURE_TO_DELETE_THIS_MODERATOR' />
                                                                                ?
                                                                            </React.Fragment>
                                                                        ),
                                                                        onAccept,
                                                                    });
                                                                }}
                                                                className={styles.closeButton}
                                                            >
                                                                <SVGPureIcon
                                                                    src={iconDelete}
                                                                    className={styles.timesIcon}
                                                                />
                                                            </button>
                                                        </Member>
                                                    )}
                                                </Mutation>
                                            ) : null
                                        )}
                                </ul>
                            </React.Fragment>
                        ) : null}
                        <FormattedMessage id='ADD_NEW_MODERATOR'>
                            {message => <div className={styles.label}>{message}</div>}
                        </FormattedMessage>
                        <UserInput
                            groupId={id}
                            key={this.state.key}
                            onChange={this.onChange}
                            maxLength={6 - currentModeratorsCount}
                        />
                        <FormattedMessage id='MAXIMUM_NUMBER_OF_MODERATORS'>
                            {msg => (
                                <span
                                    className={classnames(styles.description, {
                                        [styles.error]:
                                            newModerators.length + currentModeratorsCount >= 6 && this.state.value,
                                    })}
                                >
                                    {msg}
                                </span>
                            )}
                        </FormattedMessage>
                        <Mutation<AddModeratorsMutation, AddModeratorsMutationVariables> mutation={addMutation}>
                            {addModerators => (
                                <button
                                    className={styles.submitButton}
                                    onClick={() => {
                                        if (this.state.value) {
                                            newModerators.push(this.state.value);
                                        }

                                        addModerators({
                                            variables: {
                                                input: {
                                                    id,
                                                    moderators: newModerators,
                                                },
                                            },
                                        })
                                            .catch(err => {
                                                confirm({
                                                    acceptButtonLabel: <FormattedMessage id='OK' />,
                                                    body: err.message,
                                                });
                                            })
                                            .finally(() =>
                                                this.setState(prevState => ({ key: prevState.key + 1, value: '' }))
                                            );
                                    }}
                                    disabled={
                                        (newModerators.length === 0 && !this.state.value) ||
                                        newModerators.length + currentModeratorsCount > 6
                                    }
                                >
                                    <SVGIcon src={addToGroup} />
                                    <FormattedMessage id='BUTTON_LABEL_SUBMIT' />
                                </button>
                            )}
                        </Mutation>
                    </div>
                )}
            </AlertContext.Consumer>
        );
    }
}

export default Styled(defaultStyles)(ModeratorsSettings);
